<template>
  <div class="calm trans-form-page">
    <!-- Sticky header — section breadcrumb + save state -->
    <header class="form-header">
      <div class="form-header__inner">
        <div class="form-header__left">
          <button class="c-btn c-btn--ghost c-btn--icon" @click="handleCancel()" :title="$t('close')">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="form-header__title">
            <span class="eyebrow">{{ isEdit ? $t("edit") : $t("new") }}</span>
            <h1>{{ $t("menu.trans") }}</h1>
          </div>
        </div>
        <div class="form-header__right">
          <span class="crumb" v-if="activeSection">
            <span class="crumb__num">{{ activeSection.num }}</span>
            <span class="crumb__label">{{ activeSection.title }}</span>
          </span>
          <span class="save-state" :class="saveStateClass">
            <i :class="saveStateIcon"></i>
            <span>{{ saveStateLabel }}</span>
          </span>
        </div>
      </div>
    </header>

    <div class="form-page__grid">
      <!-- Left: scrollspy section nav -->
      <aside class="section-nav">
        <div class="section-nav__inner">
          <p class="section-nav__eyebrow">Sections</p>
          <nav>
            <a
              v-for="s in sections"
              :key="s.id"
              :href="'#section-' + s.id"
              class="section-nav__item"
              :class="{ 'is-active': activeId === s.id }"
              @click.prevent="scrollToSection(s.id)"
            >
              <span class="section-nav__num">{{ s.num }}</span>
              <span class="section-nav__label">
                <span class="section-nav__title">{{ s.title }}</span>
                <span class="section-nav__hint">{{ s.hint }}</span>
              </span>
              <span class="section-nav__bar" aria-hidden="true"></span>
            </a>
          </nav>
        </div>
      </aside>

      <!-- Main form column -->
      <main class="form-main">
        <validation-observer v-slot="{ handleSubmit, invalid }" ref="formValidator">
          <form @submit.prevent="handleSubmit(() => submit({ andNew: false }))">
            <!-- SECTION 1 — Owner & Insurance -->
            <section id="section-1" class="form-section" ref="sec1">
              <header class="form-section__header">
                <span class="section-num">01</span>
                <div>
                  <h2>{{ "เจ้าของงาน & ประกัน" }}</h2>
                  <p class="section-sub">เลือกตัวแทน แบบคำนวณค่าคอมฯ และสินค้าประกันที่ขาย</p>
                </div>
              </header>

              <div class="field-grid field-grid--4">
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.sale_date") }}</label>
                  <base-input
                    :name="$t('lmg.sale_date')"
                    rules="required"
                    class="ed-input"
                  >
                    <flat-picker
                      slot-scope="{ focus, blur }"
                      @on-open="focus"
                      @on-close="blur"
                      :config="{ allowInput: true }"
                      class="form-control datepicker"
                      v-model="trans.saleDate"
                    ></flat-picker>
                  </base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.ttype") }}</label>
                  <base-input :name="$t('lmg.ttype')" rules="required" class="ed-input">
                    <el-select v-model="ttype" :placeholder="$t('lmg.ttype')" filterable>
                      <el-option
                        v-for="o in tableTtype"
                        :key="o.ID"
                        :label="o.Name"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.owner_person") }}</label>
                  <base-input :name="$t('lmg.owner_person')" rules="required" class="ed-input">
                    <el-select
                      v-model="trans.ownerPersonID"
                      :placeholder="$t('lmg.owner_person')"
                      filterable
                    >
                      <el-option
                        v-for="o in tablePersonFilter"
                        :key="o.ID"
                        :label="(o.FName || '') + ' ' + (o.LName || '')"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure") }}</label>
                  <!--
                    New tree/breadcrumb product picker (Dev-A). The trigger is a
                    button, not an <input>, so vee-validate's `rules="required"`
                    wrapper cannot bind to it. Required is enforced instead by an
                    explicit guard at the top of submit() (see requireInsurance()).
                    `sellable-ids` gates confirmation to true sellable leaves only.
                  -->
                  <product-picker
                    :value="trans.insuranceID"
                    :sellable-ids="sellableLeafIds"
                    :loading="!catalogReady"
                    @input="onPickInsurance"
                  ></product-picker>
                </div>

                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.comm") }}</label>
                  <base-input class="ed-input">
                    <el-select v-model="trans.comProfileID" :placeholder="$t('lmg.comm')" filterable clearable>
                      <el-option
                        v-for="o in tableComm"
                        :key="o.ID"
                        :label="o.Name"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">ประเภทงาน</label>
                  <div class="chip-group">
                    <button
                      type="button"
                      class="chip"
                      :class="{ 'is-on': trans.renew == 1 }"
                      @click="trans.renew = 1"
                    >งานใหม่</button>
                    <button
                      type="button"
                      class="chip"
                      :class="{ 'is-on': trans.renew == 2 }"
                      @click="trans.renew = 2"
                    >ต่ออายุ</button>
                  </div>
                </div>
              </div>
            </section>

            <!-- SECTION 2 — Customer -->
            <section id="section-2" class="form-section" ref="sec2">
              <header class="form-section__header">
                <span class="section-num">02</span>
                <div>
                  <h2>ลูกค้า</h2>
                  <p class="section-sub">ข้อมูลผู้เอาประกัน — ค้นหาด้วยเลขบัตรเพื่อดึงข้อมูลเดิมอัตโนมัติ</p>
                </div>
              </header>

              <div class="field-grid field-grid--4">
                <div class="field field--1">
                  <label class="field__label">ประเภทบัตร</label>
                  <base-input class="ed-input">
                    <el-select v-model="trans.idCardType" placeholder="—">
                      <el-option
                        v-for="o in idCardTypes"
                        :key="o.ID"
                        :label="o.Name"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--3">
                  <label class="field__label">{{ $t("lmg.card_number") || "เลขบัตร" }}</label>
                  <base-input
                    v-model="trans.customerCardNumber"
                    class="ed-input"
                    placeholder="เลขประจำตัว 13 หลัก / หนังสือเดินทาง / เลขนิติบุคคล"
                  ></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">คำนำหน้า</label>
                  <base-input class="ed-input">
                    <el-select v-model="trans.customerPrefix" placeholder="—">
                      <el-option
                        v-for="o in tablePname"
                        :key="o.ID"
                        :label="o.Name"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field" :class="isJuridical ? 'field--2' : 'field--1'">
                  <label class="field__label">{{ isJuridical ? "ชื่อบริษัท / นิติบุคคล" : ($t("lmg.fname") || "ชื่อ") }}</label>
                  <base-input v-model="trans.customerFname" class="ed-input"></base-input>
                </div>

                <div v-if="!isJuridical" class="field field--1">
                  <label class="field__label">{{ $t("lmg.lname") || "นามสกุล" }}</label>
                  <base-input v-model="trans.customerLname" class="ed-input"></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">โทรศัพท์</label>
                  <base-input v-model="trans.customerTel" class="ed-input mono" placeholder="0X-XXXX-XXXX"></base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">อีเมล</label>
                  <base-input v-model="trans.customerEmail" class="ed-input" placeholder="name@domain.com"></base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">วันเกิด</label>
                  <base-input class="ed-input">
                    <flat-picker
                      slot-scope="{ focus, blur }"
                      @on-open="focus"
                      @on-close="blur"
                      :config="{ allowInput: true }"
                      class="form-control datepicker"
                      v-model="trans.customerDateOfBirth"
                    ></flat-picker>
                  </base-input>
                </div>
              </div>

              <div class="subrule"><span>ที่อยู่</span></div>

              <div class="field-grid field-grid--4">
                <div class="field field--4">
                  <label class="field__label">ที่อยู่</label>
                  <base-input v-model="trans.customerAddress" class="ed-input"></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">จังหวัด</label>
                  <base-input class="ed-input">
                    <el-select
                      v-model="trans.customerProvinceID"
                      placeholder="—"
                      filterable
                      @change="onProvinceChange"
                    >
                      <el-option
                        v-for="o in ProvinceTable"
                        :key="o.code"
                        :label="o.name_th"
                        :value="o.code"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">อำเภอ/เขต</label>
                  <base-input class="ed-input">
                    <el-select
                      v-model="trans.customerDistrictID"
                      placeholder="—"
                      filterable
                      @change="onDistrictChange"
                    >
                      <el-option
                        v-for="o in DistrictTable"
                        :key="o.code"
                        :label="o.name_th"
                        :value="o.code"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">ตำบล/แขวง</label>
                  <base-input class="ed-input">
                    <el-select
                      v-model="trans.customerSubDistrictID"
                      placeholder="—"
                      filterable
                      @change="onSubDistrictChange"
                    >
                      <el-option
                        v-for="o in SubDistrictTable"
                        :key="o.code"
                        :label="o.name_th"
                        :value="o.code"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">รหัสไปรษณีย์</label>
                  <base-input v-model="trans.customerPostCode" class="ed-input mono"></base-input>
                </div>
              </div>
            </section>

            <!-- SECTION 3 — Financials & Dates -->
            <section id="section-3" class="form-section" ref="sec3">
              <header class="form-section__header">
                <span class="section-num">03</span>
                <div>
                  <h2>การเงิน & วันคุ้มครอง</h2>
                  <p class="section-sub">ยอดเบี้ย ภาษี ค่าคอมฯ และช่วงเวลาคุ้มครอง</p>
                </div>
              </header>

              <div class="field-grid field-grid--4">
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.trans_amount") }}</label>
                  <base-input
                    v-model="trans.amount"
                    class="ed-input ed-input--money"
                    rules="required"
                    :name="$t('lmg.trans_amount')"
                  ></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.summary_amount") }}</label>
                  <base-input
                    v-model="trans.amountAll"
                    class="ed-input ed-input--money"
                  ></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ getTaxPercent }}</label>
                  <base-input v-model="trans.taxAmount" class="ed-input ed-input--money" readonly></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.whTax") }}</label>
                  <base-input v-model="trans.whTax" class="ed-input ed-input--money"></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ getCommissionPercent }}</label>
                  <base-input v-model="trans.commisionAmount" class="ed-input ed-input--money" readonly></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.net_amount") }}</label>
                  <base-input v-model="trans.netAmount" class="ed-input ed-input--money" readonly></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.moreDiscount") }}</label>
                  <base-input v-model="trans.moreDiscount" class="ed-input ed-input--money"></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.extra_charge") }}</label>
                  <base-input v-model="trans.extraCharge" class="ed-input ed-input--money"></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.pay_amount") }}</label>
                  <base-input v-model="trans.payAmount" class="ed-input ed-input--money" readonly></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.comm_incentive") }}</label>
                  <base-input v-model="trans.incentive" class="ed-input ed-input--money"></base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.payment_comm") }}</label>
                  <base-input v-model="trans.initialCom" class="ed-input ed-input--money"></base-input>
                </div>

                <div class="field field--1"></div>

                <div class="field field--2">
                  <label class="field__label">วันเริ่มคุ้มครอง</label>
                  <base-input class="ed-input" :name="$t('lmg.start_date')" rules="required">
                    <flat-picker
                      slot-scope="{ focus, blur }"
                      @on-open="focus"
                      @on-close="blur"
                      :config="{ allowInput: true }"
                      class="form-control datepicker"
                      v-model="trans.startDate"
                    ></flat-picker>
                  </base-input>
                </div>

                <div class="field field--2">
                  <label class="field__label">วันสิ้นสุดคุ้มครอง</label>
                  <base-input class="ed-input" :name="$t('lmg.end_date')" rules="required">
                    <flat-picker
                      slot-scope="{ focus, blur }"
                      @on-open="focus"
                      @on-close="blur"
                      :config="{ allowInput: true }"
                      class="form-control datepicker"
                      v-model="trans.endDate"
                    ></flat-picker>
                  </base-input>
                </div>
              </div>
            </section>

            <!-- SECTION 4 — Product-typed detail -->
            <section id="section-4" class="form-section" ref="sec4">
              <header class="form-section__header">
                <span class="section-num">04</span>
                <div>
                  <h2>รายละเอียดสินค้า</h2>
                  <p class="section-sub">
                    ช่องกรอกเปลี่ยนตามประเภทสินค้าที่เลือกในหัวข้อ 01
                  </p>
                </div>
                <span class="product-chip" v-if="productTypeLabel">{{ productTypeLabel }}</span>
              </header>

              <!-- Motor -->
              <div class="field-grid field-grid--4" v-if="dataType == 1">
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.policy_number") }}</label>
                  <base-input
                    v-model="trans.policyNumber"
                    class="ed-input mono"
                    rules="required"
                    :name="$t('lmg.policy_number')"
                  ></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.car_model") }}</label>
                  <base-input v-model="trans.carModel" class="ed-input"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.chassis_number") }}</label>
                  <base-input v-model="trans.chassisNumber" class="ed-input mono"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.license_plate") }}</label>
                  <base-input v-model="trans.licencePlate" class="ed-input mono"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.car_year") }}</label>
                  <base-input v-model="trans.carRegisterYear" class="ed-input mono"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.insure_sum") }}</label>
                  <base-input v-model="trans.sumInsured" class="ed-input ed-input--money"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.car_type") }}</label>
                  <div class="chip-group">
                    <button
                      type="button"
                      class="chip"
                      v-for="o in carType"
                      :key="o.ID"
                      :class="{ 'is-on': trans.engine == o.ID }"
                      @click="trans.engine = o.ID"
                    >{{ o.Name }}</button>
                  </div>
                </div>
              </div>

              <!-- Health -->
              <div class="field-grid field-grid--4" v-else-if="dataType == 2">
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.policy_number") }}</label>
                  <base-input
                    v-model="trans.policyNumber"
                    class="ed-input mono"
                    rules="required"
                    :name="$t('lmg.policy_number')"
                  ></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_type") }}</label>
                  <base-input v-model="trans.insuranceTypeDesc" class="ed-input"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.insure_plan") }}</label>
                  <base-input v-model="trans.insurancePlan" class="ed-input"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.age") }}</label>
                  <base-input v-model="trans.customerAge" class="ed-input mono"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.detail") }}</label>
                  <base-input v-model="trans.detail" class="ed-input"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_sum") }}</label>
                  <base-input v-model="trans.sumInsured" class="ed-input ed-input--money"></base-input>
                </div>
              </div>

              <!-- General Casualty -->
              <div class="field-grid field-grid--4" v-else-if="dataType == 3">
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.policy_number") }}</label>
                  <base-input
                    v-model="trans.policyNumber"
                    class="ed-input mono"
                    rules="required"
                    :name="$t('lmg.policy_number')"
                  ></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_type") }}</label>
                  <base-input v-model="trans.insuranceTypeDesc" class="ed-input"></base-input>
                </div>
                <div class="field field--4">
                  <label class="field__label">{{ $t("lmg.billing_address") }}</label>
                  <base-input v-model="trans.buildingAddress" class="ed-input"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.code_disaster") }}</label>
                  <base-input v-model="trans.codeDisaster" class="ed-input mono"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_sum") }}</label>
                  <base-input v-model="trans.sumInsured" class="ed-input ed-input--money"></base-input>
                </div>
              </div>

              <!-- Motor Casualty -->
              <div class="field-grid field-grid--4" v-else-if="dataType == 4">
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.policy_number") }}</label>
                  <base-input
                    v-model="trans.policyNumber"
                    class="ed-input mono"
                    rules="required"
                    :name="$t('lmg.policy_number')"
                  ></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.car_model") }}</label>
                  <base-input v-model="trans.carModel" class="ed-input"></base-input>
                </div>
                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.insure_sum") }}</label>
                  <base-input v-model="trans.sumInsured" class="ed-input ed-input--money"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.chassis_number") }}</label>
                  <base-input v-model="trans.chassisNumber" class="ed-input mono"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.license_plate") }}</label>
                  <base-input v-model="trans.licencePlate" class="ed-input mono"></base-input>
                </div>
                <div class="field field--4">
                  <label class="field__label">{{ $t("lmg.insurance_scope") }}</label>
                  <base-input v-model="trans.insuranceScope" class="ed-input"></base-input>
                </div>
              </div>

              <!-- Other / พรบ (dataType 5) — mirrors V1 type-5 fields -->
              <div class="field-grid field-grid--4" v-else-if="dataType == 5">
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.policy_number") }}</label>
                  <base-input
                    v-model="trans.policyNumber"
                    class="ed-input mono"
                    rules="required"
                    :name="$t('lmg.policy_number')"
                  ></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_type") }}</label>
                  <base-input
                    v-model="trans.insuranceTypeDesc"
                    class="ed-input"
                    rules="required"
                    :name="$t('lmg.insure_type')"
                  ></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_plan") }}</label>
                  <base-input
                    v-model="trans.insurancePlan"
                    class="ed-input"
                    rules="required"
                    :name="$t('lmg.insure_plan')"
                  ></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.note") }}</label>
                  <base-input v-model="trans.detail" class="ed-input"></base-input>
                </div>
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.insure_sum") }}</label>
                  <base-input
                    v-model="trans.sumInsured"
                    class="ed-input ed-input--money"
                    rules="required"
                    :name="$t('lmg.insure_sum')"
                  ></base-input>
                </div>
              </div>
            </section>

            <!-- SECTION 5 — Attachments & Notes -->
            <section id="section-5" class="form-section" ref="sec5">
              <header class="form-section__header">
                <span class="section-num">05</span>
                <div>
                  <h2>หมายเหตุและไฟล์แนบ</h2>
                  <p class="section-sub">แนบหลักฐานการโอนหรือเอกสารประกอบ และบันทึกเพิ่มเติม</p>
                </div>
              </header>

              <div class="field-grid field-grid--1">
                <!-- Pay-at-sale payment method (item 4). Mirrors V1's live
                     block TransactionTables.vue:714-756 — same options/labels,
                     same `== 3` (โอนเงิน) transfer condition for the bank
                     picker. Payload fields initialPaymentVia/paymentBankID are
                     already declared in emptyTrans() and appended in
                     buildFormData(); this UI only sets them. -->
                <div class="field field--2">
                  <label class="field__label">{{ $t("lmg.payment_method") }}</label>
                  <base-input class="ed-input">
                    <el-select
                      v-model="trans.initialPaymentVia"
                      :placeholder="$t('lmg.payment_method')"
                      filterable
                      popper-class="calm-pop"
                    >
                      <el-option
                        v-for="o in paymentType"
                        :key="o.ID"
                        :label="o.Name"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--2" v-if="trans.initialPaymentVia == 3">
                  <label class="field__label">{{ $t("lmg.bank") }}</label>
                  <base-input class="ed-input">
                    <el-select
                      v-model="trans.paymentBankID"
                      :placeholder="$t('lmg.bank')"
                      filterable
                      popper-class="calm-pop"
                    >
                      <el-option
                        v-for="o in tableBank"
                        :key="o.ID"
                        :label="o.Name"
                        :value="o.ID"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="field field--1">
                  <label class="field__label">{{ $t("lmg.note") }}</label>
                  <textarea
                    v-model="trans.note"
                    class="ed-textarea"
                    rows="3"
                    placeholder="รายละเอียดเพิ่มเติม (ไม่บังคับ)"
                  ></textarea>
                </div>

                <div class="field field--1">
                  <label class="field__label">แนบไฟล์หลักฐาน</label>
                  <div class="file-drop">
                    <file-input @change="onFilesChange"></file-input>
                    <p class="file-hint" v-if="trans.tfimg && trans.tfimg.name">
                      <i class="fas fa-paperclip"></i>
                      <span>{{ trans.tfimg.name }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <!-- Footer spacer so sticky totals bar doesn't overlap last field -->
            <div class="sticky-footer-spacer" aria-hidden="true"></div>
          </form>
        </validation-observer>
      </main>
    </div>

    <!-- Sticky totals + actions -->
    <footer class="totals-bar">
      <div class="totals-bar__inner">
        <div class="totals">
          <div class="total">
            <span class="total__label">Net</span>
            <span class="total__value">{{ fmtMoney(trans.netAmount) }}</span>
          </div>
          <div class="total total--accent">
            <span class="total__label">Pay</span>
            <span class="total__value">{{ fmtMoney(trans.payAmount) }}</span>
          </div>
          <div class="total">
            <span class="total__label">Commission</span>
            <span class="total__value">{{ fmtMoney(trans.commisionAmount) }}</span>
          </div>
        </div>
        <div class="actions">
          <button type="button" class="c-btn c-btn--ghost" @click="handleCancel()" :disabled="saving">
            {{ $t("close") }}
          </button>
          <button
            type="button"
            class="c-btn c-btn--outline"
            @click="submit({ andNew: true })"
            :disabled="saving"
          >
            <i class="fas fa-plus mr-1" v-if="!saving"></i>
            <i class="fas fa-spinner fa-spin mr-1" v-else></i>
            {{ $t("save_continue") }}
          </button>
          <button
            type="button"
            class="c-btn c-btn--primary"
            @click="submit({ andNew: false })"
            :disabled="saving"
          >
            <i class="fas fa-check mr-1" v-if="!saving"></i>
            <i class="fas fa-spinner fa-spin mr-1" v-else></i>
            {{ $t("save") }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import FileInput from "@/components/Inputs/FileInput";
// Element UI is NOT globally registered in this app — components must be
// imported + registered per-file. Without this the page's <el-select>/<el-option>
// (sales channel, agent, insurance, commission, province/district/sub-district)
// are unknown elements and render as nothing, so those dropdowns "disappear".
import { Select, Option } from "element-ui";
// Tree/breadcrumb product picker (Dev-A, asrth/lmg-fn#43). Replaces the long
// flat ShowName <el-select>. Props: value (insuranceID, v-model), sellableIds
// (valid sellable leaf IDs), loading. Emits `input` with the chosen leaf NODE
// object ({ ID, Name, DataType, ... }) or null on clear.
import ProductPicker from "@/components/lmg/insurance/ProductPicker.vue";

const ID_CARD_TYPES = [
  { ID: 1, Name: "บัตรประชาชน" },
  { ID: 2, Name: "หนังสือเดินทาง" },
  { ID: 3, Name: "นิติบุคคล" },
];

const CAR_TYPES = [
  { ID: 1, Name: "รถยนต์ทั่วไป" },
  { ID: 2, Name: "รถยนต์ไฟฟ้า" },
];

// Abbreviated — matches the common options used in V1.
const PNAME = [
  { ID: 0, Name: "ไม่ระบุ", Group: 1 },
  { ID: 3, Name: "นาย", Group: 1 },
  { ID: 4, Name: "นางสาว", Group: 1 },
  { ID: 5, Name: "นาง", Group: 1 },
  { ID: 6, Name: "คุณ", Group: 1 },
  { ID: 8, Name: "MR", Group: 2 },
  { ID: 7, Name: "Miss", Group: 2 },
  { ID: 9, Name: "MRS", Group: 2 },
  { ID: 12, Name: "หจก.", Group: 3 },
  { ID: 13, Name: "บริษัท", Group: 3 },
];

function emptyTrans() {
  return {
    transactionUpperID: "",
    ownerPersonID: "",
    comProfileID: "",
    insuranceID: "",
    amount: "",
    initialCom: "",
    initialPayment: "",
    initialPaymentVia: "",
    paymentBankID: "",
    policyNumber: "",
    licencePlate: "",
    startDate: "",
    endDate: "",
    customerID: 0,
    customerCardNumber: "",
    customerFname: "",
    customerLname: "",
    customerPrefix: "",
    customerGender: "",
    customerDateOfBirth: "",
    customerTel: "",
    customerEmail: "",
    idCardType: 1,
    note: "",
    nBilling: 1,
    tfimg: "",
    incentive: "",
    transactionDataType: 1,
    carModel: "",
    carRegisterYear: "",
    sumInsured: "",
    buildingAddress: "",
    chassisNumber: "",
    codeDisaster: "",
    insuranceScope: "",
    insurancePlan: "",
    customerAge: "",
    detail: "",
    insuranceYear: "",
    insuranceTypeDesc: "",
    saleDate: "",
    amountAll: "",
    commisionPercent: "",
    commisionAmount: "",
    taxAmount: "",
    taxPercent: "",
    netAmount: "",
    payAmount: "",
    billCharge: "",
    renew: 1,
    policyNumberUpper: "",
    moreDiscount: "",
    whTax: "",
    extraCharge: "",
    customerAddress: "",
    customerSubDistrictID: "",
    customerSubDistrict: "",
    customerDistrictID: "",
    customerDistrict: "",
    customerProvinceID: "",
    customerProvince: "",
    customerPostCode: "",
    engine: 1,
  };
}

export default {
  name: "TransactionV2Form",
  components: {
    flatPicker,
    FileInput,
    ProductPicker,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      trans: emptyTrans(),
      // The full leaf node object chosen via <product-picker>. Single source for
      // dataType (with catalogById fallback). Null when no product is selected.
      selectedInsurance: null,
      ttype: "",
      saving: false,
      saveState: "idle", // 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
      activeId: 1,
      carType: CAR_TYPES,
      idCardTypes: ID_CARD_TYPES,
      pname: PNAME,
      // Pay-at-sale payment-method options (item 4). Mirrors V1 exactly:
      // TransactionTables.vue:2608-2614 — same IDs/labels.
      paymentType: [
        { ID: 1, Name: "เงินสด" },
        { ID: 2, Name: "เช็ค" },
        { ID: 3, Name: "โอนเงิน" },
        { ID: 4, Name: "เงินสำรองจ่าย" },
        { ID: 5, Name: "บัตรเครดิต" },
      ],
      sections: [
        { id: 1, num: "01", title: "เจ้าของงาน & ประกัน", hint: "ตัวแทน คอมฯ สินค้า" },
        { id: 2, num: "02", title: "ลูกค้า", hint: "ข้อมูลและที่อยู่" },
        { id: 3, num: "03", title: "การเงิน & วันที่", hint: "ยอด ภาษี คุ้มครอง" },
        { id: 4, num: "04", title: "รายละเอียดสินค้า", hint: "ฟิลด์ตามประเภท" },
        { id: 5, num: "05", title: "หมายเหตุ", hint: "แนบไฟล์" },
      ],
      scrollHandler: null,
    };
  },

  computed: {
    ...mapGetters({
      tableInsure: "Insure/getData",
      tablePerson: "Person/getData",
      tableComm: "Comm/getData",
      tableTtype: "Ttype/getData",
      tableBank: "Bank/getData",
      ProvinceTable: "Address/getProvince",
      DistrictTable: "Address/getDistrict",
      SubDistrictTable: "Address/getSubDistrict",
    }),
    ...mapGetters("Insure", {
      // Map<ID, node> over the FULL catalog tree — the single authoritative
      // source for resolving a leaf's DataType (see dataType()).
      catalogById: "catalogById",
      // Root nodes of the catalog tree; used to know the tree has loaded.
      catalogRoots: "catalogRoots",
    }),
    // True once the catalog tree is loaded so the picker can browse/breadcrumb.
    catalogReady() {
      return !!(this.catalogRoots && this.catalogRoots.length > 0);
    },
    // IDs of the real sellable leaves (server leaves-list already encodes the
    // leaf + active rule). The picker only lets these be confirmed, so an
    // intermediate / pseudo-leaf node can never be stamped onto a transaction.
    sellableLeafIds() {
      return (this.tableInsure || []).map((o) => o.ID);
    },
    isEdit() {
      return !!this.$route.params.id;
    },
    tablePersonFilter() {
      if (this.ttype != 0 && this.ttype !== "") {
        return (this.tablePerson || []).filter((v) => v.UserTypeID == this.ttype);
      }
      return this.tablePerson || [];
    },
    tablePname() {
      if (this.trans.idCardType == "") return this.pname;
      return this.pname.filter((p) => p.Group == this.trans.idCardType);
    },
    // นิติบุคคล (idCardType=3, คำนำหน้า บจก./หจก.) — ใช้ชื่อบริษัทช่องเดียว ไม่มีนามสกุล.
    isJuridical() {
      return this.trans.idCardType == 3;
    },
    dataType() {
      // SINGLE SOURCE: resolve the product type from the catalog, never the
      // leaves-list. Prefer the node captured on selection; otherwise look it up
      // in catalogById (covers Save&New keep + any value set without a node).
      // This removes the leaves-list-vs-tree skew that could silently post the
      // wrong transactionDataType.
      // Number() coercion: catalogById is Number-keyed; a string insuranceID
      // (e.g. from a route param on a future edit path) would silently miss and
      // default to Motor — coerce so the lookup always hits for a valid id.
      const n =
        this.selectedInsurance ||
        (this.catalogById &&
          this.catalogById.get(Number(this.trans.insuranceID)));
      const t = n && n.DataType;
      // Return the TRUE DataType of the selected product. Do NOT silently
      // default to Motor (1) — that mislabels พรบ / combined products. When
      // genuinely unresolved, return "" so the product-detail grids guard
      // (and transactionDataType is sent empty rather than wrong).
      return t || "";
    },
    productTypeLabel() {
      const m = {
        1: "รถยนต์ / Motor",
        2: "สุขภาพ / Health",
        3: "ทรัพย์สิน / General Casualty",
        4: "ยานยนต์วินาศภัย / Motor Casualty",
        5: "อื่น ๆ / Other",
      };
      return m[this.dataType] || "";
    },
    getTaxPercent() {
      if (!this.trans.taxPercent) return this.$t("lmg.tax_amount");
      return this.$t("lmg.tax_amount") + " · " + this.trans.taxPercent + "%";
    },
    getCommissionPercent() {
      if (!this.trans.commisionPercent) return this.$t("lmg.commission_discount") || "ส่วนลด";
      return (this.$t("lmg.commission_discount") || "ส่วนลด") + " · " + this.trans.commisionPercent + "%";
    },
    activeSection() {
      return this.sections.find((s) => s.id === this.activeId);
    },
    saveStateClass() {
      return {
        "save-state--dirty": this.saveState === "dirty",
        "save-state--saving": this.saveState === "saving",
        "save-state--saved": this.saveState === "saved",
        "save-state--error": this.saveState === "error",
      };
    },
    saveStateIcon() {
      switch (this.saveState) {
        case "saving": return "fas fa-spinner fa-spin";
        case "saved": return "fas fa-check-circle";
        case "dirty": return "fas fa-circle";
        case "error": return "fas fa-exclamation-circle";
        default: return "far fa-circle";
      }
    },
    saveStateLabel() {
      switch (this.saveState) {
        case "saving": return "กำลังบันทึก…";
        case "saved": return "บันทึกแล้ว";
        case "dirty": return "ยังไม่บันทึก";
        case "error": return "บันทึกไม่สำเร็จ";
        default: return "พร้อมบันทึก";
      }
    },
  },

  watch: {
    // นิติบุคคล → ใช้ชื่อบริษัทช่องเดียว; ล้างนามสกุลที่อาจค้างจากบุคคลธรรมดา.
    "trans.idCardType": function (val) {
      if (val == 3) this.trans.customerLname = "";
    },
    trans: {
      deep: true,
      handler() {
        if (this.saveState === "idle" || this.saveState === "saved") {
          this.saveState = "dirty";
        }
      },
    },

    // ---- Item 1: auto-default the commission profile from the chosen owner.
    // The V2 person list (Person/getData, = tablePerson) already returns
    // ComprofileID. Find the person by ID and set comProfileID; guard when not
    // found / 0.
    "trans.ownerPersonID": function (val) {
      const p = (this.tablePerson || []).find((x) => x.ID == val);
      if (p && p.ComprofileID) {
        this.trans.comProfileID = p.ComprofileID;
      }
      // Item 3: owner change also re-runs the financial calc (V1:2783-2814).
      this.recalcCommission();
    },

    // ---- Item 2: customer auto-fill by national-ID / tax-ID on renewal.
    // Mirrors V1 TransactionTables.vue:2669-2706 exactly — including the
    // listDistrict/listSubDistrict cascade BEFORE assigning the address fields.
    "trans.customerCardNumber": {
      handler: function (val) {
        if (val && val.length > 8) {
          this.checkCustomer({
            idCardType: this.trans.idCardType,
            idCardNumber: val,
          }).then((res) => {
            if (res.data.Data != null) {
              let d = res.data.Data[0];
              this.trans.customerID = d.ID;
              this.trans.customerDateOfBirth = d.DateOfBirth;
              this.trans.customerEmail = d.Email;
              this.trans.customerGender = d.Gender;
              this.trans.customerFname = d.FName;
              this.trans.customerLname = d.LName;
              this.trans.customerPrefix = d.PrefixName;
              this.trans.customerTel = d.Tel;

              this.listDistrict({ province_code: d.ProvinceID });
              this.listSubDistrict({ district_code: d.DistrictID });

              this.trans.customerAddress = d.Address;
              this.trans.customerProvinceID = d.ProvinceID;
              this.trans.customerDistrictID = d.DistrictID;
              this.trans.customerSubDistrictID = d.SubDistrictID;
              this.trans.customerPostCode = d.PostCode;
            } else {
              this.trans.customerID = 0;
            }
          });
        }
      },
      deep: true,
    },

    // ---- Item 3: remaining recalc triggers (V1 had a dup watcher each).
    "trans.amount": function () {
      this.recalcCommission();
    },
    "trans.amountAll": function () {
      this.recalcCommission();
    },
    "trans.insuranceID": function () {
      this.recalcCommission();
    },
    "trans.whTax": function () {
      this.recalcCommission();
    },
    "trans.moreDiscount": function () {
      this.recalcCommission();
    },
    "trans.extraCharge": function () {
      this.recalcCommission();
    },
  },

  async mounted() {
    // Default to today's date for sale_date.
    this.trans.saleDate = this.getEndDate ? this.getEndDate() : "";
    // listInsure() => sellable leaves list (drives sellableLeafIds).
    // loadCatalog() => full tree for the picker's browse + breadcrumb.
    // Both are needed; fire them together. Don't block on loadCatalog — the
    // picker shows its loading state via :loading until catalogReady is true.
    await this.listInsure();
    this.loadCatalog();
    await this.listComm();
    this.listPerson();
    this.listTtype();
    this.listBank();
    this.listProvince();
    this.saveState = "idle";

    // Scrollspy — track which section is in view.
    this.scrollHandler = () => {
      const y = window.scrollY + 160;
      let current = 1;
      for (const s of this.sections) {
        const el = this.$refs["sec" + s.id];
        if (el && el.offsetTop <= y) current = s.id;
      }
      this.activeId = current;
    };
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  },

  beforeDestroy() {
    if (this.scrollHandler) window.removeEventListener("scroll", this.scrollHandler);
  },

  methods: {
    ...mapActions({
      createData: "TransactionV2/createData",
      // Financial auto-calc (item 3): GET comProfile/cal via the V2 module.
      CalCommision: "TransactionV2/CalCommision",
      // Customer renewal auto-fill (item 2): GET v2/customer/id/check.
      checkCustomer: "Customer/checkID",
      listInsure: "Insure/listData",
      loadCatalog: "Insure/loadCatalog",
      listComm: "Comm/listData",
      listBank: "Bank/listData",
      listPerson: "Person/listData",
      listTtype: "Ttype/listData",
      listProvince: "Address/listProvince",
      listDistrict: "Address/listDistrict",
      listSubDistrict: "Address/listSubDistrict",
    }),

    // ---- Item 3: shared, debounced financial recalc (mirrors V1's 7 dup
    // watchers TransactionTables.vue:2708-2939 collapsed into one). When the
    // required inputs are present, dispatch CalCommision with the SAME payload
    // V1 sends and assign the server-computed outputs from res.data.Data[0].
    recalcCommission() {
      if (this._calTimer) clearTimeout(this._calTimer);
      this._calTimer = setTimeout(() => {
        if (
          this.trans.insuranceID === "" ||
          this.trans.amount === "" ||
          this.trans.amountAll === "" ||
          this.trans.ownerPersonID === "" ||
          this.trans.ownerPersonID == 0
        )
          return;
        this.CalCommision({
          insuranceID: this.trans.insuranceID,
          ownerPersonID: this.trans.ownerPersonID,
          amount: this.trans.amount,
          amountAll: this.trans.amountAll,
          whTax: this.trans.whTax,
          moreDiscount: this.trans.moreDiscount,
          extraCharge: this.trans.extraCharge,
        })
          .then((res) => {
            const d = res.data.Data[0];
            this.trans.commisionAmount = d.CommissionAmount;
            this.trans.commisionPercent = d.CommissionPercent;
            this.trans.netAmount = d.NetAmount;
            this.trans.taxAmount = d.TaxAmount;
            this.trans.payAmount = d.PayAmount;
            this.trans.taxPercent = d.TaxPercent;
          })
          .catch(() => {
            // V1 just clears a flag here — no-op (mirror).
          });
      }, 300);
    },

    handleCancel() {
      if (this.saveState === "dirty") {
        swal.fire({
          title: "ยกเลิกการกรอก?",
          text: "ข้อมูลที่ยังไม่บันทึกจะหายไป",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "ยกเลิก",
          cancelButtonText: "กลับไปกรอก",
        }).then((r) => {
          if (r.value) this.$router.push({ name: "TransV2List" });
        });
        return;
      }
      this.$router.push({ name: "TransV2List" });
    },

    scrollToSection(id) {
      const el = this.$refs["sec" + id];
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    },

    // <product-picker> emits the chosen leaf NODE object (or null on clear).
    // Keep trans.insuranceID a bare leaf ID (the value contract / post shape),
    // and cache the node so dataType resolves without a catalog round-trip.
    onPickInsurance(node) {
      if (node) {
        this.trans.insuranceID = node.ID;
        this.selectedInsurance = node;
      } else {
        this.trans.insuranceID = "";
        this.selectedInsurance = null;
      }
    },

    // Required-field guard for the picker (caveat #4). The picker trigger is a
    // button, so vee-validate's wrapper can't bind to it. Block submit and warn
    // (Thai) when no product is selected. Returns true when OK to proceed.
    requireInsurance() {
      if (!this.trans.insuranceID) {
        swal.fire({
          icon: "warning",
          title: this.$t("lmg.insure_required"),
          timer: 2000,
          showConfirmButton: false,
        });
        this.scrollToSection(1);
        return false;
      }
      return true;
    },

    onFilesChange(files) {
      this.trans.tfimg = files && files[0] ? files[0] : "";
    },

    onProvinceChange() {
      this.trans.customerDistrictID = "";
      this.trans.customerSubDistrictID = "";
      const p = (this.ProvinceTable || []).find((x) => x.code == this.trans.customerProvinceID);
      this.trans.customerProvince = p ? p.name_th : "";
      if (this.trans.customerProvinceID) {
        this.listDistrict({ province_code: this.trans.customerProvinceID });
      }
    },
    onDistrictChange() {
      this.trans.customerSubDistrictID = "";
      const d = (this.DistrictTable || []).find((x) => x.code == this.trans.customerDistrictID);
      this.trans.customerDistrict = d ? d.name_th : "";
      if (this.trans.customerDistrictID) {
        this.listSubDistrict({ district_code: this.trans.customerDistrictID });
      }
    },
    onSubDistrictChange() {
      const s = (this.SubDistrictTable || []).find((x) => x.code == this.trans.customerSubDistrictID);
      if (s) {
        this.trans.customerSubDistrict = s.name_th;
        this.trans.customerPostCode = s.zip_code;
      }
    },

    fmtMoney(v) {
      const n = Number(v);
      if (!isFinite(n) || n === 0) return "—";
      return new Intl.NumberFormat("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },

    buildFormData() {
      const form = new FormData();
      const append = (k, v) => form.append(k, v == null ? "" : v);
      append("ownerPersonID", this.trans.ownerPersonID);
      append("insuranceID", this.trans.insuranceID);
      append("amount", this.trans.amount);
      append("initialCom", this.trans.initialCom);
      append("initialPayment", this.trans.initialPayment);
      append("initialPaymentVia", this.trans.initialPaymentVia);
      append("paymentBankID", this.trans.paymentBankID);
      append("policyNumber", this.trans.policyNumber);
      append("licencePlate", this.trans.licencePlate);
      append("startDate", this.trans.startDate);
      append("endDate", this.trans.endDate);
      append("customerID", this.trans.customerID);
      append("idCardType", this.trans.idCardType);
      append("customerCardNumber", this.trans.customerCardNumber);
      append("customerFname", this.trans.customerFname);
      append("customerLname", this.trans.customerLname);
      append("customerPrefix", this.trans.customerPrefix);
      append("customerGender", this.trans.customerGender);
      append("customerDateOfBirth", this.trans.customerDateOfBirth);
      append("customerTel", this.trans.customerTel);
      append("customerEmail", this.trans.customerEmail);
      append("note", this.trans.note);
      append("saleDate", this.trans.saleDate);
      append("transactionDataType", this.dataType);
      append("carModel", this.trans.carModel);
      append("carRegisterYear", this.trans.carRegisterYear);
      append("sumInsured", this.trans.sumInsured);
      append("buildingAddress", this.trans.buildingAddress);
      append("chassisNumber", this.trans.chassisNumber);
      append("codeDisaster", this.trans.codeDisaster);
      append("insuranceScope", this.trans.insuranceScope);
      append("insurancePlan", this.trans.insurancePlan);
      append("customerAge", this.trans.customerAge);
      append("insuranceTypeDesc", this.trans.insuranceTypeDesc);
      append("detail", this.trans.detail || "0");
      append("insuranceYear", this.trans.insuranceYear);
      append("transactionUpperID", this.trans.transactionUpperID);
      append("amountAll", this.trans.amountAll);
      append("commisionPercent", this.trans.commisionPercent);
      append("commisionAmount", this.trans.commisionAmount);
      append("taxPercent", this.trans.taxPercent);
      append("taxAmount", this.trans.taxAmount);
      append("netAmount", this.trans.netAmount);
      append("payAmount", this.trans.payAmount);
      // Mirror V1 (TransactionTables.vue:3183): only send billCharge when not
      // billing-mode 3, otherwise 0.
      append("billCharge", this.trans.nBilling != 3 ? this.trans.billCharge : 0);
      append("renew", this.trans.renew);
      append("policyNumberUpper", this.trans.policyNumberUpper);
      append("moreDiscount", this.trans.moreDiscount);
      append("whTax", this.trans.whTax);
      append("engine", this.trans.engine);
      // Mirror V1 (TransactionTables.vue:3190-3196): force a single billing when
      // paid in full at sale (via 4) or initial payment equals the amount.
      append(
        "nBilling",
        this.trans.initialPaymentVia == 4 ||
          this.trans.initialPayment == this.trans.amount
          ? 1
          : this.trans.nBilling
      );
      append("tfimg", this.trans.tfimg);
      append("incentive", this.trans.incentive);
      append("extraCharge", this.trans.extraCharge);
      append("customerAddress", this.trans.customerAddress);
      append("customerSubDistrictID", this.trans.customerSubDistrictID);
      append("customerSubDistrict", this.trans.customerSubDistrict);
      append("customerDistrictID", this.trans.customerDistrictID);
      append("customerDistrict", this.trans.customerDistrict);
      append("customerProvinceID", this.trans.customerProvinceID);
      append("customerProvince", this.trans.customerProvince);
      append("customerPostCode", this.trans.customerPostCode);
      return form;
    },

    async submit({ andNew }) {
      // Picker required-field guard first — the button trigger isn't bound to
      // vee-validate, so enforce insurance presence explicitly here.
      if (!this.requireInsurance()) return;
      // Run vee-validate manually so Save & New path goes through the same gate.
      const ok = this.$refs.formValidator ? await this.$refs.formValidator.validate() : true;
      if (!ok) {
        swal.fire({
          icon: "warning",
          title: "กรุณากรอกข้อมูลให้ครบถ้วน",
          text: "ช่องที่บังคับยังว่างอยู่",
          timer: 1800,
          showConfirmButton: false,
        });
        return;
      }
      this.saving = true;
      this.saveState = "saving";
      try {
        const resp = await this.createData(this.buildFormData());
        this.saveState = "saved";
        swal.fire({
          icon: "success",
          title: this.$t("create_success"),
          timer: 1200,
          showConfirmButton: false,
        });
        if (andNew) {
          // "บันทึกต่อเนื่อง": keep EVERYTHING from the just-saved policy
          // (customer, owner/agent, dates, channel, vehicle/product-config,
          // renew, comProfile, ...) and clear only the three fields staff must
          // re-enter for the next policy: policy number, premium, insurance
          // type. Also clear the money outputs derived from those inputs so the
          // form never shows stale commission/net/tax from the previous policy.
          this.trans = {
            ...this.trans, // keep all current values
            // Chain this next tx to the one we just saved (as before).
            transactionUpperID: (resp && resp.data && resp.data.Datum) || "",
            // --- the 3 fields staff must re-enter ---
            policyNumber: "", // เลขกรมธรรม์
            insuranceID: "", // ประเภทประกัน (product picker)
            amount: "", // เบี้ยประกัน
            amountAll: "", // summary premium
            // --- derived money outputs (recalc guard skips while amount/
            //     insuranceID are blank, so clear them to avoid stale values) ---
            commisionAmount: "",
            commisionPercent: "",
            taxAmount: "",
            taxPercent: "",
            netAmount: "",
            payAmount: "",
          };
          // The product picker was cleared along with insuranceID.
          this.selectedInsurance = null;
          // Reset vee-validate so cleared required fields (premium, policy
          // number, etc.) don't flash red before the user touches them.
          this.$nextTick(() => {
            if (this.$refs.formValidator) this.$refs.formValidator.reset();
          });
          // Suppress the saved-then-dirty flicker — the next user edit will
          // flip this back to 'dirty' via the existing watcher.
          this.saveState = "idle";
          this.scrollToSection(1);
        } else {
          this.$router.push({ name: "TransV2List" });
        }
      } catch (err) {
        this.saveState = "error";
        const key = err && err.response && err.response.data && err.response.data.Msg;
        swal.fire({
          icon: "error",
          title: key ? this.$t(key) : "บันทึกไม่สำเร็จ",
          text: (err && err.response && err.response.data && err.response.data.Text) || "",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/*
 * Form page consumes the shared Calm system (src/assets/scss/v2/calm.scss),
 * which is imported globally and declares the design tokens on `.calm`. The
 * page root carries `class="calm"`, so those tokens cascade in here.
 *
 * No local token mirror and no font @import live in this file anymore — both
 * are provided by calm.scss. The structural/layout rules below reference the
 * page's historical local names (--canvas, --ink, --accent, …); the thin
 * alias block beneath maps each onto its global --c-* equivalent so the
 * single source of truth for every value is calm.scss.
 */
.trans-form-page {
  /* Aliases onto the global Calm tokens — values come from calm.scss, not here. */
  --canvas: var(--c-bg);
  --surface: var(--c-surface);
  --ink: var(--c-text);
  --ink-soft: var(--c-text-soft);
  --rule: var(--c-border);
  --rule-strong: var(--c-border-strong);
  --accent: var(--c-blue);
  --accent-soft: var(--c-blue-soft);
  --accent-deep: var(--c-blue-deep);
  --success: var(--c-success);
  --warning: var(--c-warning);
  --danger: var(--c-danger);

  --font-display: var(--c-font);
  --font-body: var(--c-font);
  --font-mono: var(--c-font-mono);

  background: var(--canvas);
  color: var(--ink);
  font-family: var(--font-body);
  min-height: 100vh;
  padding-bottom: 80px;
  font-variant-numeric: tabular-nums;
  line-height: 1.55;
}

/* ------------ Sticky header ------------ */
.form-header {
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--c-surface);
  backdrop-filter: saturate(180%) blur(8px);
  border-bottom: 1px solid var(--rule);
}
.form-header__inner {
  max-width: 1360px;
  margin: 0 auto;
  padding: 14px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.form-header__left {
  display: flex;
  align-items: center;
  gap: 18px;
}
.form-header__title .eyebrow {
  display: block;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-soft);
  font-weight: 500;
}
.form-header__title h1 {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 20px;
  margin: 0;
  letter-spacing: -0.01em;
}
.form-header__right {
  display: flex;
  align-items: center;
  gap: 20px;
}
.crumb {
  display: flex;
  align-items: baseline;
  gap: 10px;
  color: var(--ink-soft);
  font-size: 13px;
}
.crumb__num {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--accent);
}
.save-state {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 6px 12px;
  border: 1px solid var(--rule);
  border-radius: 999px;
  color: var(--ink-soft);
  background: var(--surface);
}
.save-state--dirty { color: var(--warning); border-color: rgba(182, 122, 26, 0.3); }
.save-state--saving { color: var(--ink); border-color: var(--rule-strong); }
.save-state--saved { color: var(--success); border-color: rgba(61, 107, 75, 0.3); background: rgba(61, 107, 75, 0.06); }
.save-state--error { color: var(--danger); border-color: rgba(168, 58, 42, 0.3); background: rgba(168, 58, 42, 0.06); }

/* ------------ Grid ------------ */
.form-page__grid {
  max-width: 1360px;
  margin: 0 auto;
  padding: 32px 28px 120px;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 56px;
  align-items: start;
}

/* ------------ Section nav ------------ */
.section-nav {
  position: sticky;
  top: 84px;
  align-self: start;
}
.section-nav__eyebrow {
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--rule);
}
.section-nav nav { display: flex; flex-direction: column; gap: 2px; }
.section-nav__item {
  position: relative;
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 14px;
  padding: 12px 14px;
  text-decoration: none;
  color: var(--ink-soft);
  transition: color 0.2s, background 0.2s;
  border-radius: 8px;
}
.section-nav__item:hover { background: var(--accent-soft); }
.section-nav__num {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  transition: opacity 0.2s, color 0.2s;
}
.section-nav__label { display: flex; flex-direction: column; gap: 2px; }
.section-nav__title { font-size: 14px; font-weight: 500; color: var(--ink); letter-spacing: 0; }
.section-nav__hint { font-size: 12px; color: var(--ink-soft); letter-spacing: 0; }
.section-nav__bar {
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 2px;
  background: transparent;
  transition: background 0.2s;
}
.section-nav__item:hover { color: var(--ink); }
.section-nav__item:hover .section-nav__bar { background: var(--rule-strong); }
.section-nav__item:hover .section-nav__num { opacity: 1; color: var(--ink); }
.section-nav__item.is-active .section-nav__bar { background: var(--accent); }
.section-nav__item.is-active .section-nav__num { color: var(--accent); opacity: 1; }
.section-nav__item.is-active .section-nav__title { color: var(--ink); font-weight: 600; }

/* ------------ Section content ------------ */
.form-section {
  position: relative;
  padding: 28px 24px 32px;
  background: var(--surface);
  border: 1px solid var(--rule);
  border-radius: 12px;
  margin-bottom: 18px;
  box-shadow: 0 1px 2px rgba(44, 62, 80, 0.04);
  opacity: 0;
  animation: fade-up 0.5s ease forwards;
}
.form-section:nth-child(1) { animation-delay: 0.05s; }
.form-section:nth-child(2) { animation-delay: 0.1s; }
.form-section:nth-child(3) { animation-delay: 0.15s; }
.form-section:nth-child(4) { animation-delay: 0.2s; }
.form-section:nth-child(5) { animation-delay: 0.25s; }
.form-section:last-child { border-bottom: 0; }

@keyframes fade-up {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
}

.form-section__header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: start;
  margin-bottom: 28px;
}
.section-num {
  font-family: var(--font-mono);
  font-weight: 500;
  font-size: 32px;
  line-height: 1;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.form-section__header h2 {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 20px;
  margin: 4px 0 6px;
  letter-spacing: -0.005em;
  color: var(--ink);
}
.section-sub {
  font-size: 13px;
  color: var(--ink-soft);
  margin: 0;
  line-height: 1.5;
  max-width: 560px;
}
.product-chip {
  align-self: center;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0;
  padding: 6px 12px;
  border: 1px solid var(--accent-soft);
  color: var(--accent-deep);
  background: var(--accent-soft);
  border-radius: 6px;
}

.subrule {
  margin: 28px 0 18px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.subrule::before,
.subrule::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--rule);
}
.subrule span {
  font-size: 12px;
  letter-spacing: 0;
  color: var(--ink-soft);
  font-weight: 500;
}

/* ------------ Field grid ------------ */
.field-grid {
  display: grid;
  gap: 20px 24px;
}
.field-grid--1 { grid-template-columns: 1fr; }
.field-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.field--1 { grid-column: span 1; }
.field--2 { grid-column: span 2; }
.field--3 { grid-column: span 3; }
.field--4 { grid-column: span 4; }

.field { display: flex; flex-direction: column; gap: 6px; }
.field__label {
  /* Thai labels can't take uppercase + wide tracking — keep them plain Sarabun. */
  font-size: 12px;
  letter-spacing: 0;
  color: var(--ink-soft);
  font-weight: 500;
  margin: 0;
}

/* ------------ Input overrides (targets Argon's base-input) ------------ */
/*
 * Make sure the validation-provider wrapper, the input-group and the inner
 * el-input are full width — base-input's defaults can collapse to inline-block,
 * which on some Element-UI versions causes the el-select to render at 0 width
 * (the dropdown then looks "missing" in the form).
 */
.ed-input ::v-deep > * { width: 100%; }
.ed-input ::v-deep .form-group { margin-bottom: 0 !important; width: 100%; }
.ed-input ::v-deep .input-group { box-shadow: none !important; background: transparent; width: 100%; display: flex; }
.ed-input ::v-deep .input-group-text { background: transparent; border: 0; padding: 0; }

.ed-input ::v-deep .form-control,
.ed-input ::v-deep .el-input__inner,
.ed-textarea {
  background: var(--surface) !important;
  border: 1px solid var(--rule) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: var(--ink) !important;
  font-family: var(--font-body);
  font-size: 13.5px;
  height: 40px;
  padding: 9px 12px !important;
  transition: border-color 0.15s ease;
  width: 100%;
}
.ed-input ::v-deep .form-control:focus,
.ed-input ::v-deep .el-input__inner:focus,
.ed-textarea:focus {
  border-color: var(--accent) !important;
  box-shadow: 0 0 0 3px rgba(59, 110, 168, 0.12) !important;
  outline: none !important;
}

.ed-input ::v-deep .el-select { width: 100%; display: block; }
.ed-input ::v-deep .el-select .el-input { width: 100%; display: block; }
.ed-input ::v-deep .el-select .el-input__inner { height: 40px !important; line-height: 40px !important; }
.ed-input ::v-deep .el-input__icon { line-height: 40px; }

.ed-input.mono ::v-deep .form-control,
.ed-input.mono ::v-deep .el-input__inner {
  font-family: var(--font-mono);
  letter-spacing: 0.02em;
}
.ed-input--money ::v-deep .form-control {
  font-family: var(--font-mono);
  text-align: right;
  padding-right: 14px !important;
  font-variant-numeric: tabular-nums;
}

.ed-textarea {
  width: 100%;
  height: auto;
  min-height: 96px;
  resize: vertical;
  font-family: var(--font-body);
  line-height: 1.5;
}

/* Inline validation feedback from vee-validate */
.ed-input ::v-deep .invalid-feedback { font-size: 11px; color: var(--danger); margin-top: 4px; }
.ed-input ::v-deep .is-invalid .form-control,
.ed-input ::v-deep .is-invalid .el-input__inner { border-color: var(--danger) !important; }

/* ------------ Chip group ------------ */
.chip-group { display: flex; gap: 6px; flex-wrap: wrap; }
.chip {
  padding: 8px 14px;
  font-size: 13px;
  font-family: var(--font-body);
  background: var(--surface);
  border: 1px solid var(--rule);
  color: var(--ink-soft);
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s;
}
.chip:hover { color: var(--accent-deep); border-color: var(--accent); background: var(--accent-soft); }
.chip.is-on {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
}

/* ------------ File drop ------------ */
.file-drop {
  padding: 20px;
  border: 1.5px dashed var(--rule-strong);
  border-radius: 10px;
  background: var(--accent-soft);
}
.file-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--ink-soft);
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ------------ Sticky totals footer ------------ */
.sticky-footer-spacer { height: 100px; }
.totals-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--surface);
  border-top: 1px solid var(--rule);
  box-shadow: 0 -8px 24px -16px rgba(44, 62, 80, 0.12);
  z-index: 40;
  backdrop-filter: saturate(180%) blur(8px);
}
.totals-bar__inner {
  max-width: 1360px;
  margin: 0 auto;
  padding: 16px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.totals { display: flex; gap: 32px; }
.total { display: flex; flex-direction: column; gap: 2px; }
.total__label {
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-soft);
}
.total__value {
  font-family: var(--font-mono);
  font-weight: 500;
  font-size: 18px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.total--accent .total__value { color: var(--accent); font-size: 22px; }

.actions { display: flex; gap: 8px; align-items: center; }
/* Footer buttons now use the shared Kit classes (.c-btn / --ghost / --outline
   / --primary) from calm.scss — no bespoke button styles needed here. */

/* ------------ Responsive ------------ */
@media (max-width: 1024px) {
  .form-page__grid { grid-template-columns: 1fr; gap: 24px; padding: 24px 20px 120px; }
  .section-nav { position: static; display: flex; }
  .section-nav__inner { width: 100%; }
  .section-nav nav {
    flex-direction: row;
    overflow-x: auto;
    gap: 8px;
    padding-bottom: 8px;
    scrollbar-width: thin;
  }
  .section-nav__item {
    padding: 10px 14px;
    border: 1px solid var(--rule);
    border-radius: 2px;
    white-space: nowrap;
  }
  .section-nav__bar { display: none; }
  .section-nav__item.is-active { border-color: var(--accent); background: var(--accent-soft); }
}
@media (max-width: 768px) {
  .field-grid--4 { grid-template-columns: 1fr; }
  .field--1, .field--2, .field--3, .field--4 { grid-column: span 1; }
  .form-section__header {
    grid-template-columns: auto 1fr;
    gap: 16px;
  }
  .product-chip { grid-column: 1 / -1; justify-self: start; margin-top: 8px; }
  .section-num { font-size: 40px; }
  .form-section__header h2 { font-size: 22px; }
  .totals-bar__inner { flex-direction: column; align-items: stretch; gap: 16px; }
  .totals { justify-content: space-between; gap: 16px; }
  .actions { justify-content: flex-end; }
}
</style>
